import React, { useEffect, useState } from 'react';
import { Lock, LogOut, ThumbsDown, ThumbsUp, RefreshCw } from 'lucide-react';
import { apiService, FeedbackListItem, FeedbackStats } from '../utils/api';

type FeedbackFilter = 'all' | 'positive' | 'negative';

const FeedbackInbox: React.FC = () => {
  const [items, setItems] = useState<FeedbackListItem[]>([]);
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [filter, setFilter] = useState<FeedbackFilter>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const loadFeedback = async (nextFilter: FeedbackFilter = filter) => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const [feedbackList, feedbackStats] = await Promise.all([
        apiService.getChatFeedback(nextFilter, 100),
        apiService.getChatFeedbackStats(),
      ]);
      setItems(feedbackList.items);
      setStats(feedbackStats);
    } catch (err: any) {
      const message = err.response?.data?.detail || err.message || 'Failed to load feedback';
      setError(message);
      if (err.response?.status === 401) {
        setIsAuthenticated(false);
        setItems([]);
        setStats(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeedback(filter);
  }, [filter, isAuthenticated]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError('Enter both username and password.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await apiService.loginFeedbackAdmin({ username: username.trim(), password });
      setIsAuthenticated(true);
      setPassword('');
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to log in');
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await apiService.logoutFeedbackAdmin();
    } catch (_err) {
      // Best effort logout; local state still clears.
    } finally {
      setIsAuthenticated(false);
      setItems([]);
      setStats(null);
      setError(null);
      setPassword('');
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-xl mx-auto p-4 pt-10">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-gray-900 p-3 text-white">
                <Lock className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">Unlock Feedback Inbox</h2>
                <p className="text-sm text-gray-500">Sign in with the feedback admin username and password for this instance.</p>
              </div>
            </div>

            <form onSubmit={handleLogin} className="mt-6 space-y-4">
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
                className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-800 focus:border-megi-red focus:outline-none"
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-800 focus:border-megi-red focus:outline-none"
              />
              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-megi-red px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-megi-red-dark"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-4 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Feedback Inbox</h2>
            <p className="text-sm text-gray-500">Review chat votes and notes without opening Postgres.</p>
          </div>
          <button
            type="button"
            onClick={() => loadFeedback(filter)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleLogout}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>

        {stats && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-sm text-gray-500">Total feedback</p>
              <p className="mt-2 text-2xl font-bold text-gray-800">{stats.total}</p>
            </div>
            <div className="rounded-xl border border-green-200 bg-green-50 p-4">
              <p className="text-sm text-green-700">Positive</p>
              <p className="mt-2 text-2xl font-bold text-green-800">{stats.positive}</p>
            </div>
            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-700">Negative</p>
              <p className="mt-2 text-2xl font-bold text-red-800">{stats.negative}</p>
            </div>
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
              <p className="text-sm text-blue-700">With notes</p>
              <p className="mt-2 text-2xl font-bold text-blue-800">{stats.with_notes}</p>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {(['all', 'positive', 'negative'] as FeedbackFilter[]).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setFilter(option)}
              className={`rounded-full px-4 py-2 text-sm transition-colors ${
                filter === option
                  ? 'bg-megi-red text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'
              }`}
            >
              {option === 'all' ? 'All feedback' : option === 'positive' ? 'Positive only' : 'Negative only'}
            </button>
          ))}
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500">
            Loading feedback...
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500">
            No feedback yet for this filter.
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item) => (
              <article key={item.response_id} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${
                      item.value === 'positive'
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}
                  >
                    {item.value === 'positive' ? <ThumbsUp className="w-3 h-3" /> : <ThumbsDown className="w-3 h-3" />}
                    {item.value}
                  </span>
                  <span className="rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-600">
                    {item.model}
                  </span>
                  <span className="rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-600">
                    session {item.session_id}
                  </span>
                  <span className="text-xs text-gray-500">
                    updated {new Date(item.feedback_updated_at).toLocaleString()}
                  </span>
                </div>

                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">User message</p>
                    <div className="mt-2 rounded-lg bg-gray-50 p-3 text-sm text-gray-800">
                      {item.user_message}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Assistant response</p>
                    <div className="mt-2 rounded-lg bg-gray-50 p-3 text-sm text-gray-800 whitespace-pre-wrap">
                      {item.assistant_response}
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Note</p>
                    <div className="mt-2 rounded-lg border border-gray-200 p-3 text-sm text-gray-700 min-h-[72px]">
                      {item.comment?.trim() ? item.comment : 'No note left.'}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Trace</p>
                    <div className="mt-2 rounded-lg border border-gray-200 p-3 text-sm text-gray-700">
                      {item.langfuse_trace_id ? item.langfuse_trace_id : 'No trace id'}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          This view is protected by a backend-validated login session cookie.
        </div>
      </div>
    </div>
  );
};

export default FeedbackInbox;
